import { firestore, functions } from "utils/firebase";

/**
 * Create a new campaign
 */
export const createCampaign = async (campaignData) => {
  try {
    const createCampaignFunction = functions.httpsCallable("createCampaign");
    const result = await createCampaignFunction(campaignData);
    return result.data;
  } catch (error) {
    console.error("Error creating campaign:", error);
    throw error;
  }
};

/**
 * Get all campaigns from Firestore
 */
export const getCampaigns = async () => {
  try {
    const campaignsRef = firestore
      .collection("campaigns")
      .orderBy("createdAt", "desc");
    const snapshot = await campaignsRef.get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()?.toISOString(),
      startsAt: doc.data().startsAt?.toDate()?.toISOString(),
      endsAt: doc.data().endsAt?.toDate()?.toISOString(),
    }));
  } catch (error) {
    console.error("Error getting campaigns:", error);
    throw error;
  }
};

/**
 * Get a single campaign by ID
 */
export const getCampaignById = async (campaignId) => {
  try {
    const doc = await firestore.collection("campaigns").doc(campaignId).get();

    if (!doc.exists) {
      throw new Error("Campaign not found");
    }

    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()?.toISOString(),
      startsAt: doc.data().startsAt?.toDate()?.toISOString(),
      endsAt: doc.data().endsAt?.toDate()?.toISOString(),
    };
  } catch (error) {
    console.error("Error getting campaign:", error);
    throw error;
  }
};

/**
 * Update campaign
 */
export const updateCampaign = async (campaignId, data) => {
  try {
    const campaignRef = firestore.collection("campaigns").doc(campaignId);
    const updateData = {
      ...data,
      updatedAt: new Date(),
    };

    // Convert dates to Timestamps
    if (updateData.startsAt) {
      updateData.startsAt = new Date(updateData.startsAt);
    }
    if (updateData.endsAt) {
      updateData.endsAt = new Date(updateData.endsAt);
    }

    await campaignRef.update(updateData);
    return { success: true };
  } catch (error) {
    console.error("Error updating campaign:", error);
    throw error;
  }
};

/**
 * Delete campaign
 */
export const deleteCampaign = async (campaignId) => {
  try {
    await firestore.collection("campaigns").doc(campaignId).delete();
    return { success: true };
  } catch (error) {
    console.error("Error deleting campaign:", error);
    throw error;
  }
};

/**
 * Generate promo codes for a campaign
 */
export const generatePromoCodes = async (campaignId, quantity) => {
  try {
    const generateCodesFunction = functions.httpsCallable("generatePromoCodes");
    const result = await generateCodesFunction({ campaignId, quantity });
    return result.data;
  } catch (error) {
    console.error("Error generating promo codes:", error);
    throw error;
  }
};

/**
 * Get campaign statistics
 */
export const getCampaignStats = async (campaignId) => {
  try {
    const getStatsFunction = functions.httpsCallable("getCampaignStats");
    const result = await getStatsFunction({ campaignId });
    return result.data;
  } catch (error) {
    console.error("Error getting campaign stats:", error);
    throw error;
  }
};

/**
 * Get all promo codes for a campaign
 */
export const getPromoCodes = async (campaignId) => {
  try {
    const snapshot = await firestore
      .collection("promoCodes")
      .where("campaignId", "==", campaignId)
      .orderBy("createdAt", "desc")
      .limit(100)
      .get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()?.toISOString(),
      claimedAt: doc.data().claimedAt?.toDate()?.toISOString(),
    }));
  } catch (error) {
    console.error("Error getting promo codes:", error);
    throw error;
  }
};

/**
 * Get claims for a campaign
 */
export const getCampaignClaims = async (campaignId) => {
  try {
    const snapshot = await firestore
      .collection("promoClaims")
      .where("campaignId", "==", campaignId)
      .orderBy("claimedAt", "desc")
      .limit(100)
      .get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      claimedAt: doc.data().claimedAt?.toDate()?.toISOString(),
    }));
  } catch (error) {
    console.error("Error getting campaign claims:", error);
    throw error;
  }
};

/**
 * Toggle campaign active status
 */
export const toggleCampaignStatus = async (campaignId, isActive) => {
  try {
    await firestore
      .collection("campaigns")
      .doc(campaignId)
      .update({ isActive, updatedAt: new Date() });
    return { success: true };
  } catch (error) {
    console.error("Error toggling campaign status:", error);
    throw error;
  }
};
